<script setup lang="ts">
import type { SwitchRootEmits, SwitchRootProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import {
    SwitchRoot,
    SwitchThumb,
    useForwardPropsEmits,
} from 'reka-ui';
import { useFieldControl } from '@/components/base/_internal/control';
import { cn } from '@/utils/shadcn/lib/utils';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<Props>(), {
    class: '',
    name: undefined,
    required: false,
    invalid: false,
    value: 'on',
    disabled: false,
});

const emits = defineEmits<SwitchRootEmits>();

interface Props extends SwitchRootProps {
    class?: HTMLAttributes['class']
    name?: string
    required?: boolean
    invalid?: boolean
}

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const field = useFieldControl(() => props, () => 'auto');
</script>

<template>
    <SwitchRoot
        v-slot="slotProps"
        data-slot="switch"
        v-bind="forwarded"
        :id="field.id"
        :disabled="field.disabled"
        :required="field.required"
        :aria-describedby="field.describedBy"
        :aria-invalid="field.invalid"
        class="sph-form-control"
        :class="cn(
            'peer flex-none data-[state=checked]:bg-input-dark data-[state=unchecked]:bg-input-dark inline-flex h-6 w-10! mt-px shrink-0 items-center rounded-full border border-border shadow-xs transition-all outline-none',
            props.class,
        )"
    >
        <SwitchThumb
            data-slot="switch-thumb"
            :class="cn('bg-background data-[state=unchecked]:bg-white data-[state=checked]:bg-primary pointer-events-none block size-5 rounded-full border border-primary/25 ring-0 transition-all duration-200 data-[state=checked]:translate-x-[calc(100%-3px)] data-[state=unchecked]:translate-x-[1px]')"
        >
            <slot
                name="thumb"
                v-bind="slotProps"
            ></slot>
        </SwitchThumb>
    </SwitchRoot>
</template>
